<script src="ace-table.component.ts"></script>
<div [id]="tableHTMLId" #tableWrapper>
  <!-- If the spinner is being changed, also need to update checkLoadingIndicatorOverBodyOnly() on the component -->
  @if (loading && !loadingIndicatorOverBodyOnly) {
    <div class="position-relative">
      <div class="ace-table-spinner on-top">
        <div class="bounce1"></div>
        <div class="bounce2"></div>
        <div class="bounce3"></div>
      </div>
    </div>
  }

  <div [ngClass]="{'transparent': loading && !loadingIndicatorOverBodyOnly, 'fit-spinner': loadingIndicatorOverBodyOnly}"
       [class]="'text-break' + (tableClass ? ' ' + tableClass : '')">
    <ngx-datatable
      class='bootstrap'
      [ngClass]="{'striped-rows': stripedRows}"
      [rowIdentity]="rowIdentity"
      [count]="count"
      [columnMode]="actualColumnMode"
      [columns]="columns"
      [externalPaging]="externalPaging"
      [externalSorting]="externalSorting"
      [sorts]="sorts"
      [footerHeight]="rows && rows.length ? footerHeight : 0"
      [headerHeight]="headerHeight"
      [limit]="limit"
      [rowClass]="rowClassFn"
      [loadingIndicator]="loading"
      [messages]="{
        emptyMessage: (messages?.emptyMessage || 'Table.Empty' ) | messageTranslate,
        totalMessage: (messages?.totalMessage || 'Table.Total') | messageTranslate,
        selectedMessage: (messages?.selectedMessage || 'Table.Selected') | messageTranslate
      }"
      [offset]="offset"
      [reorderable]="reorderable"
      [rowHeight]="rowHeight"
      [scrollbarV]="scrollbarV"
      [scrollbarH]="scrollbarH"
      [rows]="rows"
      [selected]="selected"
      [selectionType]="selectionType"
      [selectCheck]="selectCheck"
      [summaryRow]="summaryRow"
      [summaryPosition]="summaryPosition"
      [summaryHeight]="summaryHeight"
      (page)="pageEvent($event)"
      (select)="onSelectEvent($event)"
      (copy)="copyEvent($event)"
      (sort)="sortEvent($event)"
      (activate)="activateEvent($event)">

      @if (simplePaging) {
        <ngx-datatable-footer>
          <ng-template
            ngx-datatable-footer-template
            let-rowCount="rowCount"
            let-pageSize="pageSize"
            let-selectedCount="selectedCount"
            let-curPage="curPage"
            let-offset="offset">

            <ng-template [ngTemplateOutlet]="simplePagingTemplate"
                         [ngTemplateOutletContext]="{rowCount: rowCount, pageSize: pageSize, curPage: curPage}"/>
          </ng-template>
        </ngx-datatable-footer>
      }

      @if (showPageLimitControl) {
        <ngx-datatable-footer>
          <ng-template
            ngx-datatable-footer-template
            let-rowCount="rowCount"
            let-pageSize="pageSize"
            let-selectedCount="selectedCount"
            let-curPage="curPage"
            let-offset="offset">
            <div class="d-none d-sm-block page-count">
              @if (table.footer.selectedMessage) {
                <span>
                  {{selectedCount?.toLocaleString()}} {{table.footer.selectedMessage}} /
                </span>
              }
              {{rowCount?.toLocaleString()}} {{table.messages.totalMessage}}
            </div>

            <div class="d-none d-sm-block ms-auto me-2">
              <select (change)="onLimitChange($event.target.value)" class="mx-1">
                @for (option of pageLimitOptions; track option) {
                  <option [value]="option" [selected]="option === pageSize">
                    {{option}}
                  </option>
                }
              </select>
              <!-- Force the text to wrap as one word rather than between words as this looks better -->
              <span class="text-nowrap">{{ 'per page' | messageTranslate }}</span>
            </div>

            <div class="d-none d-sm-block">
              <app-ace-table-pager [pagerLeftArrowIcon]="table.cssClasses.pagerLeftArrow"
                               [pagerRightArrowIcon]="table.cssClasses.pagerRightArrow"
                               [pagerPreviousIcon]="table.cssClasses.pagerPrevious"
                               [pagerNextIcon]="table.cssClasses.pagerNext"
                               [page]="curPage"
                               [size]="pageSize"
                               [count]="rowCount"
                               [hidden]="(rowCount / pageSize) <= 1"
                               (change)="table.onFooterPage($event)"/>
            </div>

            <div class="d-block d-sm-none w-100">
              <ng-template [ngTemplateOutlet]="simplePagingTemplate"
                           [ngTemplateOutletContext]="{rowCount: rowCount, pageSize: pageSize, curPage: curPage}"/>
            </div>

            @if (shouldShowJumpToPage) {
              <div class="d-none d-md-flex form-group mb-0 ps-4 align-items-center">
                <form [formGroup]="jumpToPage" (ngSubmit)="gotoPage()">
                  <label [for]="jumpToPageElementId" class="mb-0 me-1 form-label">{{ 'Jump to page' | messageTranslate }}</label>
                  <input type="text"
                         class="form-control form-control-sm d-inline page-number me-1"
                         formControlName="pageNumber"
                         [id]="jumpToPageElementId"
                         [title]="getJumpToPageTooltip()"
                         [ngClass]="{'text-danger': !jumpToPage.valid}"/>
                </form>
              </div>
            }

          </ng-template>
        </ngx-datatable-footer>
      }

    </ngx-datatable>
  </div>
</div>

<ng-template #simplePagingTemplate let-rowCount="rowCount" let-pageSize="pageSize" let-curPage="curPage">
  @if (showPageLimitControl) {
    <div class="d-none d-sm-block ms-auto me-2">
      <select (change)="onLimitChange($event.target.value)" class="mx-1">
        @for (option of pageLimitOptions; track option) {
          <option [value]="option" [selected]="option === pageSize">
            {{option}}
          </option>
        }
      </select>
      {{ 'per page' | messageTranslate }}
    </div>
  }

  <div [ngClass]="{'ms-auto': !showPageLimitControl}">
    @if ((curPage > 1) || ((curPage * pageSize) < rowCount)) {
      <div class="datatable-pager">
        <ul class="pager">
          <li #prev [ngClass]="{'disabled': curPage <= 1}">
            <a role="button" aria-label="go to previous page"
               (click)="prev.classList.contains('disabled') || table.onFooterPage({page: curPage - 1})">
              <i [ngClass]="table.cssClasses.pagerLeftArrow"></i>
            </a>
          </li>
          <li #next [ngClass]="{'disabled': (curPage * pageSize) >= rowCount}">
            <a role="button" aria-label="go to next page"
               (click)="next.classList.contains('disabled') || table.onFooterPage({page: curPage + 1})">
              <i [ngClass]="table.cssClasses.pagerRightArrow"></i>
            </a>
          </li>
        </ul>
      </div>
    }
  </div>
</ng-template>
